package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.model.Course
import com.evoapps.evoshift.domain.model.CourseAssignment
import com.evoapps.evoshift.domain.model.CourseCompletion
import com.evoapps.evoshift.domain.model.CourseKey
import com.evoapps.evoshift.domain.model.CourseProgress
import com.evoapps.evoshift.domain.model.CourseResult
import com.evoapps.evoshift.domain.model.OhsConfirmation
import com.evoapps.evoshift.domain.model.OhsEntry
import com.evoapps.evoshift.domain.model.OhsRecord
import com.evoapps.evoshift.domain.model.TrainingStatus

/** The kinds of OHS document the server prints (ohsTrainingDocument). */
enum class OhsDocumentKind {
    /** Karta szkolenia wstępnego (załącznik nr 2) with the electronic confirmations. */
    CARD,
    /** Zaświadczenie o ukończeniu szkolenia okresowego (załącznik nr 3). */
    CERTIFICATE
}

/** A printed document behind a link that works for a day. */
data class OhsDocumentLink(val url: String, val fileName: String)

/**
 * SH-126: courses, their answer keys, assignments, progress and results (Courses.kt says who
 * reads what). Grading and the OHS record go through callable functions (functions/courses.js):
 * the clients cannot write results or records.
 */
interface CourseRepository {
    /** [workplaceId]'s courses: managers get drafts too, members only published ones. */
    suspend fun getCourses(workplaceId: String, includeDrafts: Boolean): Result<List<Course>>

    suspend fun getCourse(workplaceId: String, courseId: String): Result<Course>

    /** The right answers (managers). Null when the course has none yet. */
    suspend fun getKey(workplaceId: String, courseId: String): Result<CourseKey?>

    /** Saves the course and its key together (a blank id gets a new one); returns the stored course. */
    suspend fun saveCourse(course: Course, key: CourseKey): Result<Course>

    /** Removes the course with its key, assignments, results, progress, pictures and presentation files. */
    suspend fun deleteCourse(workplaceId: String, courseId: String): Result<Unit>

    /** Uploads a picture for a page; returns its Storage path. */
    suspend fun uploadImage(workplaceId: String, courseId: String, bytes: ByteArray, contentType: String): Result<String>

    suspend fun downloadImage(path: String): Result<ByteArray>

    /** SH-211: uploads a presentation file (.pptx, .odp, PDF) for a page; returns its Storage path. */
    suspend fun uploadPresentation(workplaceId: String, courseId: String, bytes: ByteArray, contentType: String): Result<String>

    /** SH-211: an uploaded presentation's content (the workplace's members, storage.rules). */
    suspend fun downloadPresentation(path: String): Result<ByteArray>

    /** A new id for a course that is not saved yet (pictures need it). */
    fun newCourseId(workplaceId: String): String

    suspend fun getAssignments(workplaceId: String, courseId: String): Result<List<CourseAssignment>>

    /** The reader's own personal assignments at [workplaceId]. */
    suspend fun getMyAssignments(workplaceId: String, userId: String, courseIds: List<String>): Result<List<CourseAssignment>>

    suspend fun assign(workplaceId: String, assignment: CourseAssignment): Result<Unit>

    suspend fun unassign(workplaceId: String, courseId: String, userId: String): Result<Unit>

    /** Everyone's results for a course (managers). */
    suspend fun getCompletions(workplaceId: String, courseId: String): Result<List<CourseCompletion>>

    /** [userId]'s own result; null before the first attempt. */
    suspend fun getCompletion(workplaceId: String, courseId: String, userId: String): Result<CourseCompletion?>

    suspend fun getProgress(workplaceId: String, courseId: String, userId: String): Result<CourseProgress?>

    /** Everyone's progress of a course (managers). */
    suspend fun getAllProgress(workplaceId: String, courseId: String): Result<List<CourseProgress>>

    /** The person records the pages seen (their own). */
    suspend fun saveProgress(workplaceId: String, progress: CourseProgress): Result<Unit>

    /**
     * Sends the answers (question id → picked indexes) for grading on the server. [confirm]: the
     * employee's confirmation of an OHS general instruction (§ 12 ust. 1).
     */
    suspend fun submit(workplaceId: String, courseId: String, version: Int, answers: Map<String, List<Int>>, confirm: Boolean): Result<CourseResult>

    /** [userId]'s OHS record; null when nothing is recorded. */
    suspend fun getOhsRecord(workplaceId: String, userId: String): Result<OhsRecord?>

    /** A manager records a part (recordOhsTraining); returns the record after it. */
    suspend fun recordOhs(workplaceId: String, userId: String, entry: OhsEntry): Result<OhsRecord>

    /** The person confirms a part (confirmOhsTraining). */
    suspend fun confirmOhs(workplaceId: String, part: OhsConfirmation): Result<OhsRecord>

    /** The karta or zaświadczenie as a PDF (in Polish, as the regulation's templates) behind a day's link (ohsTrainingDocument). */
    suspend fun ohsDocument(workplaceId: String, userId: String, kind: OhsDocumentKind): Result<OhsDocumentLink>

    /** [userId]'s training status; null when nothing counts. */
    suspend fun getTrainingStatus(workplaceId: String, userId: String): Result<TrainingStatus?>

    /** Everyone's training status at [workplaceId] (managers). */
    suspend fun getTrainingStatuses(workplaceId: String): Result<List<TrainingStatus>>
}
